import { FilterCollection } from '../../constants';
import type { FilterDrawerCollection, FilterSections } from '../../types';

export type LabelledFilterItem = {
	filter_checkboxes?: Array<{ label?: string | null; value?: string | null }> | null;
	filter_tags?: Array<{ label?: string | null; value?: string | null }> | null;
	label?: string | null;
};

export const isEmptyValue = (value: FilterDrawerCollection['value'] | undefined) => {
	if (value === undefined || value === null) return true;
	if (Array.isArray(value)) return !value.length;
	if (typeof value === 'string') return !value;
	if (typeof value === 'boolean') return !value;
	return false;
};

export const getFilterMeta = (filterSections: FilterSections, filterCollectionId: string) => {
	for (const section of filterSections || []) {
		for (const filterType of section?.filter_type || []) {
			if (filterType?.id === filterCollectionId) {
				return { section, filterType };
			}
		}
	}

	return undefined;
};

// The display label for a facet's current selection. Accepts either a
// pre-resolved `item` or the raw `filterSections` to resolve it from, so both
// the event builder (which already has the metadata) and standalone callers
// can share one implementation.
export const getFilterValueLabel = ({
	collection,
	item,
	filterSections
}: {
	collection: FilterDrawerCollection;
	item?: LabelledFilterItem | undefined;
	filterSections?: FilterSections;
}): string => {
	const { value } = collection;

	if (isEmptyValue(value)) return '';

	const resolvedItem =
		item ??
		(getFilterMeta(filterSections || [], collection.filterCollectionId)?.filterType?.item as
			LabelledFilterItem | undefined);

	if (Array.isArray(value)) {
		// Slider values are a [min, max] pair, not a list of selections.
		if (collection.filterCollection === FilterCollection.Filter_Slider) {
			return value.join('-');
		}

		const options = [
			...(resolvedItem?.filter_checkboxes || []),
			...(resolvedItem?.filter_tags || [])
		];

		// Selections can be stored as option ids (e.g. artist ids on editions);
		// report the display label so the value is meaningful downstream.
		return value
			.map(
				(selected) =>
					options.find((option) => String(option?.value) === String(selected))?.label ||
					String(selected)
			)
			.join(', ');
	}

	return String(value);
};
